import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { developmentApi } from '../api/development';
import { useToastStore } from '../stores/toastStore';
import { soundEngine } from '../lib/soundEngine';

export function useDevelopmentDefinitions() {
  return useQuery({
    queryKey: ['development', 'definitions'],
    queryFn: () => developmentApi.getDefinitions(),
    staleTime: 5000,
    refetchInterval: 15000,
  });
}

export function useDevelopmentProjects() {
  return useQuery({
    queryKey: ['development', 'projects'],
    queryFn: () => developmentApi.getProjects(),
    staleTime: 3000,
    refetchInterval: 5000,
  });
}

export function useStartProject() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation({
    mutationFn: (upgradeDefinitionId: string) => developmentApi.startProject(upgradeDefinitionId),
    onSuccess: (data) => {
      soundEngine.playPurchaseChime();
      queryClient.invalidateQueries({ queryKey: ['planet', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['civilisation'] });
      queryClient.invalidateQueries({ queryKey: ['development', 'definitions'] });
      queryClient.invalidateQueries({ queryKey: ['development', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['credits', 'transactions'] });
      addToast(`Project started: ${data.data?.project?.upgrade?.name || 'Development project'}`, 'success');
    },
    onError: (error: any) => {
      soundEngine.playErrorBuzz();
      const msg = error.response?.data?.error?.message || 'Failed to start project';
      addToast(msg, 'error');
    },
  });
}

export function useCancelProject() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation({
    mutationFn: (projectId: string) => developmentApi.cancelProject(projectId),
    onSuccess: (data) => {
      soundEngine.playPurchaseChime();
      queryClient.invalidateQueries({ queryKey: ['planet', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['civilisation'] });
      queryClient.invalidateQueries({ queryKey: ['development', 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['credits', 'transactions'] });
      addToast(`Project cancelled. Refund: ${data.data?.refund || 0} credits`, 'info');
    },
    onError: (error: any) => {
      soundEngine.playErrorBuzz();
      const msg = error.response?.data?.error?.message || 'Failed to cancel project';
      addToast(msg, 'error');
    },
  });
}

export function useCompleteProject() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation({
    mutationFn: (projectId: string) => developmentApi.completeProject(projectId),
    onSuccess: () => {
      soundEngine.playPurchaseChime();
      queryClient.invalidateQueries({ queryKey: ['planet', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['civilisation'] });
      queryClient.invalidateQueries({ queryKey: ['development', 'projects'] });
      addToast('Project completed! Slot freed.', 'success');
    },
    onError: (error: any) => {
      soundEngine.playErrorBuzz();
      const msg = error.response?.data?.error?.message || 'Failed to complete project';
      addToast(msg, 'error');
    },
  });
}

export function useForecast(upgradeDefinitionId: string | null, days?: number) {
  return useQuery({
    queryKey: ['development', 'forecast', upgradeDefinitionId, days],
    queryFn: () => {
      if (!upgradeDefinitionId) return Promise.reject('No upgrade selected');
      return developmentApi.getForecast(upgradeDefinitionId, days);
    },
    enabled: !!upgradeDefinitionId,
    staleTime: 10000,
  });
}
